import { Link } from 'react-router-dom';
import { useSeo } from '../lib/seo';

const LAST_UPDATED = '2026-05-10';

export function Terms() {
  useSeo({
    title: 'Terms of Business — Omega Trade and Finance',
    description: 'Terms governing every engagement with Omega Trade and Finance Ltd (Companies House 17206902). 2% fee floor, body-corporate only, England & Wales jurisdiction.',
    canonicalPath: '/terms',
  });
  return (
    <main>
      <section className="section legal-hero">
        <div className="container">
          <span className="eyebrow">Terms of business</span>
          <h1>How an Omega engagement works.</h1>
          <p className="lead" style={{ marginTop: 18, maxWidth: '64ch' }}>
            These Terms govern every engagement between you (the "Client") and Omega Trade and
            Finance Ltd ("Omega"). They apply alongside the written engagement letter we issue for
            each brief; the engagement letter takes precedence on engagement-specific points.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">

          <Clause title="1. Parties">
            <p>
              <strong>Omega Trade and Finance Ltd</strong>, a private company limited by shares
              registered in England & Wales with company number <strong>17206902</strong>{' '}
              ("<strong>Omega</strong>", "<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>").
            </p>
            <p>
              The "<strong>Client</strong>" is the body corporate identified in the engagement
              letter. These Terms do not create any rights for any third party under the Contracts
              (Rights of Third Parties) Act 1999.
            </p>
          </Clause>

          <Clause title="2. Eligibility">
            <ul className="checklist">
              <li>Omega engages with <strong>body corporates only</strong> — Limited Companies, PLCs and LLPs. We do not engage with sole traders, partnerships of fewer than four partners, or individuals acting in a personal capacity.</li>
              <li>Each Client must self-certify under the Financial Promotion Order 2005 as either an <strong>"investment professional"</strong> (Article 19) or a <strong>"high net worth body corporate"</strong> (Article 49).</li>
              <li>Where introductions involve regulated activity, the Client engages directly with the authorised counterparty introduced — Omega is not a party to that downstream contract.</li>
            </ul>
          </Clause>

          <Clause title="3. Services">
            <p>
              Omega provides <strong>consultancy, research, document preparation and pure introducer
              services</strong>. Every service is structured to fall outside the regulatory perimeter
              of the Financial Services and Markets Act 2000 — see our{' '}
              <Link to="/regulatory">Regulatory position</Link> page for the detailed framing under
              FSMA s.19, RAO Articles 15 & 29 and FPO s.21.
            </p>
            <p>Omega <strong>does not</strong>:</p>
            <ul className="checklist">
              <li>Carry on any regulated activity within the meaning of FSMA s.22.</li>
              <li>Advise on the merits of any specific investment, instrument, deal or counterparty.</li>
              <li>Hold client money or operate any client-money account.</li>
              <li>Arrange deals in investments other than within the scope of RAO Articles 15 and 29 exemptions.</li>
              <li>Issue, broker or guarantee any financial instrument — those are performed by the relevant authorised institution.</li>
            </ul>
          </Clause>

          <Clause title="4. Engagement letter">
            <p>
              Every engagement begins with a written engagement letter signed by both parties. The
              engagement letter records scope, deliverables, fee, payment schedule, timing and any
              engagement-specific terms. No work begins, and no fees are payable, until the
              engagement letter is signed.
            </p>
          </Clause>

          <Clause title="5. Fees">
            <ul className="checklist">
              <li>Omega's fee floor is <strong>2% of the engagement value</strong>, applied to every service line.</li>
              <li>Fees increase based on scope, expedited timelines, cross-border counterparty diligence and the volume of authorised-counterparty introductions required.</li>
              <li>For success-based introduction services, the engagement letter sets a clear success-fee trigger and amount.</li>
              <li>All fees are quoted in the agreed invoicing currency (see clause 6) and exclusive of VAT, which is charged where applicable.</li>
              <li>Fees are invoiced as <strong>"consultancy, research or introduction fees"</strong> only — never as deposits, escrow or any term that would re-characterise the service.</li>
            </ul>
          </Clause>

          <Clause title="6. Payment">
            <p>Omega accepts payment via:</p>
            <ul className="checklist">
              <li><strong>Card</strong> — American Express, Visa, Mastercard, UnionPay (processed via our Revolut Business merchant rail).</li>
              <li><strong>Bank transfer</strong> — BACS for GBP UK transfers; SWIFT for international. Each invoice carries a unique payment reference that must be quoted.</li>
            </ul>
            <p>Invoicing currencies: GBP, USD, EUR, AED, INR, SGD. Other ISO 4217 currencies on request, with the FX rate locked at engagement-letter stage.</p>
            <p>
              Standard payment terms are <strong>14 days</strong> from invoice date unless the
              engagement letter says otherwise. Late payment may attract interest at the rate set
              by the Late Payment of Commercial Debts (Interest) Act 1998.
            </p>
          </Clause>

          <Clause title="7. Confidentiality">
            <p>
              Each party will keep confidential all non-public information disclosed by the other
              party for the purposes of the engagement and use it only for those purposes. This
              clause survives termination by five years, or longer where required by AML record-keeping
              obligations.
            </p>
          </Clause>

          <Clause title="8. Intellectual property">
            <p>
              Omega retains ownership of its methodologies, templates, frameworks and any pre-existing
              know-how. Deliverables prepared specifically for the Client are licensed to the Client
              on a non-exclusive, royalty-free basis for the Client's internal business purposes once
              fees due in respect of those deliverables have been paid in full.
            </p>
            <p>
              The Client retains ownership of its own data and inputs. Omega processes Client data
              only as needed to deliver the engagement (see the <Link to="/privacy">Privacy policy</Link>).
            </p>
          </Clause>

          <Clause title="9. Liability">
            <p>
              Nothing in these Terms limits liability for death or personal injury caused by
              negligence, fraud, or any other liability that cannot be limited under English law.
            </p>
            <p>
              Subject to the above, Omega's total aggregate liability arising out of or in connection
              with an engagement, whether in contract, tort or otherwise, is capped at the fees
              actually paid by the Client to Omega under that engagement in the 12 months preceding
              the event giving rise to the claim.
            </p>
            <p>
              Omega is not liable for: (a) any indirect or consequential loss; (b) loss of profit,
              revenue or anticipated savings; (c) loss of goodwill or business opportunity; or (d)
              acts, omissions or insolvency of any authorised counterparty introduced.
            </p>
          </Clause>

          <Clause title="10. Anti-bribery, sanctions, AML">
            <p>
              Both parties will comply with the Bribery Act 2010, the Sanctions and Anti-Money
              Laundering Act 2018 and all applicable financial-crime law. Omega is a "relevant
              business" for the purposes of the Money Laundering, Terrorist Financing and Transfer
              of Funds (Information on the Payer) Regulations 2017 and conducts customer due
              diligence on every Client and material counterparty.
            </p>
          </Clause>

          <Clause title="11. Term and termination">
            <p>
              Either party may terminate an engagement on 14 days' written notice. Either party may
              terminate immediately on written notice if the other party commits a material breach
              that is not remedied within 14 days of notice, becomes insolvent, or is sanctioned by
              a competent regulator.
            </p>
            <p>
              On termination, the Client pays for work performed up to the termination date and any
              non-cancellable third-party costs already committed. Clauses that by their nature
              survive termination continue to apply.
            </p>
          </Clause>

          <Clause title="12. Force majeure">
            <p>
              Neither party is liable for failure or delay caused by events beyond reasonable
              control — including but not limited to acts of God, war, terrorism, cyber-attack,
              government action, industry-wide infrastructure outage or pandemic measures — for so
              long as the event continues.
            </p>
          </Clause>

          <Clause title="13. Governing law and jurisdiction">
            <p>
              These Terms and any engagement letter are governed by the laws of <strong>England &
              Wales</strong>. The parties submit to the exclusive jurisdiction of the courts of
              England & Wales for any dispute arising out of or in connection with these Terms,
              save that Omega may bring enforcement proceedings in any jurisdiction where the
              Client has assets.
            </p>
          </Clause>

          <Clause title="14. Notices">
            <p>
              Formal notices must be sent in writing to <Mono>enquiry@omegatrade.co.uk</Mono> and to
              the registered office address of the recipient. Email is sufficient unless the
              engagement letter requires recorded delivery.
            </p>
          </Clause>

          <Clause title="15. Variation">
            <p>
              Omega may update these Terms from time to time. Material changes are flagged on the
              portal at next sign-in (or via email for non-portal Clients) and require re-acceptance.
              The current version is dated <Mono>{LAST_UPDATED}</Mono>.
            </p>
          </Clause>

          <div className="legal-closer">
            <strong>Last updated</strong>: {LAST_UPDATED}. Send any queries to{' '}
            <Mono>enquiry@omegatrade.co.uk</Mono>. See also the{' '}
            <Link to="/regulatory">Regulatory position</Link> and{' '}
            <Link to="/privacy">Privacy policy</Link>.
          </div>
        </div>
      </section>
    </main>
  );
}

function Clause({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="legal-block">
      <h2>{title}</h2>
      <div className="body">{children}</div>
    </div>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return <span style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{children}</span>;
}
